import { useState, useEffect, useCallback } from 'react'
import { useNotification } from '../../contexts/NotificationContext'
import api from '../../services/api'
import {
  RefreshCw,
  Download,
  Link2,
  Unlink,
  Loader2,
  Search,
  ExternalLink,
  History,
  Car,
  Plus,
  X,
} from 'lucide-react'
import PortalLogo from '../../components/integracoes/PortalLogo'
import { SearchInput } from '../../components/ui'
import { portaisIds } from '../../constants/integracoes'
import { Card, Badge, Button, buttonClass } from '../../design-system'

interface VeiculoSimples {
  id: number
  placa: string
  marca: string
  modelo: string
  versao: string
  anoModelo: number
  valorVenda: number
  status: string
}

interface PublicacaoExterna {
  id: number
  portal: string
  idExterno: string
  urlAnuncio: string | null
  titulo: string | null
  preco: number | null
  placa: string | null
  marca: string | null
  modelo: string | null
  versao: string | null
  anoFabricacao: number | null
  anoModelo: number | null
  km: number | null
  cor: string | null
  veiculoId: number | null
  vinculoStatus: string
  statusPlataforma: string | null
  tipoDestaque: string | null
  visualizacoes: number
  cliques: number
  mensagens: number
  ultimaSincronizacao: string | null
  veiculoLocal: {
    id: number
    placa: string | null
    marca: string | null
    modelo: string | null
    versao: string | null
    anoModelo: number | null
    valorVenda: number | null
    status: string | null
    fotoPrincipal: string | null
  } | null
}

interface SincronizacaoHistorico {
  id: number
  portal: string
  usuarioNome: string | null
  tipo: string
  status: string
  totalProcessados: number
  totalSucesso: number
  totalErros: number
  totalNovos: number
  totalAtualizados: number
  totalVinculados: number
  mensagem: string | null
  dataInicio: string
  dataFim: string | null
  duracaoSegundos: number | null
}

// Usando portaisIds centralizado do constants/integracoes

export default function SincronizacaoView() {
  const { showApiError, showSuccess } = useNotification()
  const [loading, setLoading] = useState(true)
  const [importando, setImportando] = useState<string | null>(null)
  const [vinculando, setVinculando] = useState<number | null>(null)
  const [tab, setTab] = useState<'anuncios' | 'historico'>('anuncios')
  const [showVincularModal, setShowVincularModal] = useState<PublicacaoExterna | null>(null)
  const [showRegistrarModal, setShowRegistrarModal] = useState(false)
  const [registrando, setRegistrando] = useState(false)
  const [veiculosDisponiveis, setVeiculosDisponiveis] = useState<VeiculoSimples[]>([])
  const [buscaVeiculo, setBuscaVeiculo] = useState('')
  const [registroForm, setRegistroForm] = useState({
    portal: '',
    veiculoId: null as number | null,
    urlAnuncio: '',
    statusPlataforma: 'ATIVO',
    tipoDestaque: ''
  })

  const [anuncios, setAnuncios] = useState<PublicacaoExterna[]>([])
  const [historico, setHistorico] = useState<SincronizacaoHistorico[]>([])
  const [totalAnuncios, setTotalAnuncios] = useState(0)

  const [filtros, setFiltros] = useState({
    portal: '',
    vinculoStatus: '',
    busca: '',
    page: 1,
    pageSize: 20,
  })

  const fetchAnuncios = useCallback(async (signal?: AbortSignal) => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filtros.portal) params.append('portal', filtros.portal)
      if (filtros.vinculoStatus) params.append('vinculoStatus', filtros.vinculoStatus)
      if (filtros.busca) params.append('busca', filtros.busca)
      params.append('page', filtros.page.toString())
      params.append('pageSize', filtros.pageSize.toString())

      const response = await api.get(`/integracoes/sincronizacao/anuncios-externos?${params.toString()}`, { signal })
      setAnuncios(response.data.items)
      setTotalAnuncios(response.data.totalItems)
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        showApiError(error)
      }
    } finally {
      setLoading(false)
    }
  }, [filtros, showApiError])

  const fetchHistorico = useCallback(async (signal?: AbortSignal) => {
    try {
      const response = await api.get('/integracoes/sincronizacao/historico?pageSize=50', { signal })
      setHistorico(response.data.items)
    } catch (error: unknown) {
      if ((error as { name?: string })?.name !== 'CanceledError') {
        // Ignorar erros de histórico
      }
    }
  }, [])

  const fetchVeiculos = useCallback(async (busca: string) => {
    if (busca.length < 2) {
      setVeiculosDisponiveis([])
      return
    }
    try {
      const response = await api.get(`/veiculos?busca=${encodeURIComponent(busca)}&pageSize=10&status=DISPONIVEL,RESERVADO`)
      setVeiculosDisponiveis(response.data.items || [])
    } catch {
      setVeiculosDisponiveis([])
    }
  }, [])

  const registrarAnuncioExterno = async () => {
    if (!registroForm.portal) {
      showApiError({ message: 'Selecione um portal' })
      return
    }
    if (!registroForm.veiculoId) {
      showApiError({ message: 'Selecione um veículo' })
      return
    }

    try {
      setRegistrando(true)
      const response = await api.post('/integracoes/sincronizacao/registrar-anuncio', {
        portal: registroForm.portal,
        veiculoId: registroForm.veiculoId,
        urlAnuncio: registroForm.urlAnuncio || null,
        statusPlataforma: registroForm.statusPlataforma,
        tipoDestaque: registroForm.tipoDestaque || null
      })

      if (response.data.sucesso) {
        showSuccess('Anúncio registrado com sucesso!')
        setShowRegistrarModal(false)
        setRegistroForm({
          portal: '',
          veiculoId: null,
          urlAnuncio: '',
          statusPlataforma: 'ATIVO',
          tipoDestaque: ''
        })
        setBuscaVeiculo('')
        setVeiculosDisponiveis([])
        fetchAnuncios()
      } else {
        showApiError({ message: response.data.message })
      }
    } catch (error) {
      showApiError(error)
    } finally {
      setRegistrando(false)
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    if (tab === 'anuncios') {
      fetchAnuncios(controller.signal)
    } else {
      fetchHistorico(controller.signal)
    }
    return () => controller.abort()
  }, [tab, fetchAnuncios, fetchHistorico])

  const importarAnuncios = async (portal: string) => {
    try {
      setImportando(portal)
      const response = await api.post(`/integracoes/sincronizacao/${portal}/importar`)
      if (response.data.sucesso) {
        showSuccess(response.data.mensagem)
        fetchAnuncios()
        fetchHistorico()
      } else {
        showApiError({ message: response.data.mensagem })
      }
    } catch (error) {
      showApiError(error)
    } finally {
      setImportando(null)
    }
  }

  const vincularAnuncio = async (publicacaoExternaId: number, veiculoId: number) => {
    try {
      setVinculando(publicacaoExternaId)
      await api.post('/integracoes/sincronizacao/vincular', {
        publicacaoExternaId,
        veiculoId,
      })
      showSuccess('Anúncio vinculado com sucesso!')
      setShowVincularModal(null)
      fetchAnuncios()
    } catch (error) {
      showApiError(error)
    } finally {
      setVinculando(null)
    }
  }

  const desvincularAnuncio = async (publicacaoExternaId: number) => {
    if (!window.confirm('Remover vínculo deste anúncio?')) return

    try {
      setVinculando(publicacaoExternaId)
      await api.delete(`/integracoes/sincronizacao/vincular/${publicacaoExternaId}`)
      showSuccess('Vínculo removido com sucesso!')
      fetchAnuncios()
    } catch (error) {
      showApiError(error)
    } finally {
      setVinculando(null)
    }
  }

  const getVinculoStatusBadge = (status: string) => {
    switch (status) {
      case 'VINCULADO':
        return <Badge tone="success">Vinculado</Badge>
      case 'NAO_ENCONTRADO':
        return <Badge tone="warning">Não encontrado</Badge>
      case 'CONFLITO':
        return <Badge tone="danger">Conflito</Badge>
      default:
        return <Badge tone="neutral">Não processado</Badge>
    }
  }

  const formatCurrency = (value: number | null) => {
    if (!value) return '-'
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2">
            <RefreshCw className="w-7 h-7 text-primary-600" />
            Sincronização com Plataformas
          </h1>
          <p className="text-slate-500 mt-1">
            Importe anúncios das plataformas e vincule com seu estoque
          </p>
        </div>
        <Button onClick={() => setShowRegistrarModal(true)}>
          <Plus className="w-5 h-5" />
          Registrar Anúncio Manual
        </Button>
      </div>

      {/* Cards de Importação */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {portaisIds.map(portal => (
          <Card
            key={portal}
            hoverable
            onClick={() => importando === portal ? undefined : importarAnuncios(portal)}
            className={`p-4 ${importando === portal ? 'pointer-events-none opacity-60' : ''}`}
          >
            <div className="flex items-center gap-3">
              <PortalLogo portal={portal} size="md" />
              <div className="flex-1">
                <p className="font-semibold text-slate-900">{portal}</p>
                <p className="text-xs text-slate-400">Clique para importar</p>
              </div>
              {importando === portal ? (
                <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
              ) : (
                <Download className="w-5 h-5 text-slate-400" />
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Tabs */}
      <div className="border-b border-slate-200">
        <nav className="flex gap-4">
          <button
            onClick={() => setTab('anuncios')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-colors ${
              tab === 'anuncios'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            Anúncios Externos ({totalAnuncios})
          </button>
          <button
            onClick={() => setTab('historico')}
            className={`pb-2 text-sm font-semibold border-b-2 transition-colors ${
              tab === 'historico'
                ? 'border-primary-600 text-primary-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            <History className="w-4 h-4 inline mr-1" />
            Histórico
          </button>
        </nav>
      </div>

      {tab === 'anuncios' && (
        <>
          {/* Filtros */}
          <Card className="p-4">
            <div className="flex flex-wrap gap-4">
              <SearchInput
                value={filtros.busca}
                onChange={(value) => setFiltros({ ...filtros, busca: value, page: 1 })}
                placeholder="Buscar por placa, título, marca..."
              />
              <select
                value={filtros.portal}
                onChange={(e) => setFiltros({ ...filtros, portal: e.target.value, page: 1 })}
                className="px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todos os Portais</option>
                {portaisIds.map(portal => (
                  <option key={portal} value={portal}>{portal}</option>
                ))}
              </select>
              <select
                value={filtros.vinculoStatus}
                onChange={(e) => setFiltros({ ...filtros, vinculoStatus: e.target.value, page: 1 })}
                className="px-4 py-2.5 border border-slate-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                <option value="">Todos os Status</option>
                <option value="VINCULADO">Vinculados</option>
                <option value="NAO_ENCONTRADO">Não encontrados</option>
                <option value="NAO_PROCESSADO">Não processados</option>
              </select>
            </div>
          </Card>

          {/* Lista de Anúncios */}
          <Card className="overflow-hidden">
            {loading ? (
              <div className="py-12 text-center">
                <Loader2 className="w-8 h-8 animate-spin text-primary-600 mx-auto" />
              </div>
            ) : anuncios.length === 0 ? (
              <div className="py-12 text-center">
                <RefreshCw className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500 mb-2">Nenhum anúncio externo encontrado</p>
                <p className="text-sm text-slate-400">Clique em um portal acima para importar anúncios</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {anuncios.map(anuncio => (
                  <div key={anuncio.id} className="p-4 hover:bg-slate-50">
                    <div className="flex items-start gap-4">
                      <PortalLogo portal={anuncio.portal} size="md" className="flex-shrink-0" />

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="font-semibold text-slate-900 truncate">
                            {anuncio.titulo || `${anuncio.marca} ${anuncio.modelo}`}
                          </h3>
                          {getVinculoStatusBadge(anuncio.vinculoStatus)}
                        </div>
                        <p className="text-sm text-slate-500">
                          {anuncio.placa && `Placa: ${anuncio.placa} | `}
                          {anuncio.marca} {anuncio.modelo} {anuncio.versao} |
                          {anuncio.anoModelo && ` ${anuncio.anoModelo} |`}
                          {anuncio.km && ` ${anuncio.km.toLocaleString()} km`}
                        </p>
                        <p className="text-sm font-semibold text-slate-900 mt-1 tabular-nums">
                          {formatCurrency(anuncio.preco)}
                        </p>

                        {/* Veículo vinculado */}
                        {anuncio.veiculoLocal && (
                          <div className="mt-2 p-2 bg-emerald-50 rounded-lg flex items-center gap-3">
                            <Car className="w-4 h-4 text-emerald-600" />
                            <span className="text-sm text-emerald-800">
                              Vinculado a: {anuncio.veiculoLocal.marca} {anuncio.veiculoLocal.modelo} -
                              Placa {anuncio.veiculoLocal.placa} - {formatCurrency(anuncio.veiculoLocal.valorVenda)}
                            </span>
                          </div>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        {anuncio.urlAnuncio && (
                          <a
                            href={anuncio.urlAnuncio}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={buttonClass('secondary', 'sm')}
                          >
                            <ExternalLink className="w-4 h-4" />
                            Ver
                          </a>
                        )}

                        {anuncio.vinculoStatus === 'VINCULADO' ? (
                          <Button
                            variant="danger"
                            size="sm"
                            onClick={() => desvincularAnuncio(anuncio.id)}
                            disabled={vinculando === anuncio.id}
                          >
                            {vinculando === anuncio.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Unlink className="w-4 h-4" />
                            )}
                            Desvincular
                          </Button>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => setShowVincularModal(anuncio)}
                          >
                            <Link2 className="w-4 h-4" />
                            Vincular
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>
        </>
      )}

      {tab === 'historico' && (
        <Card className="overflow-hidden">
          {historico.length === 0 ? (
            <div className="py-12 text-center">
              <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">Nenhuma sincronização realizada ainda</p>
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-100 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  <th className="px-4 py-3 text-left font-bold">Data</th>
                  <th className="px-4 py-3 text-left font-bold">Portal</th>
                  <th className="px-4 py-3 text-left font-bold">Tipo</th>
                  <th className="px-4 py-3 text-left font-bold">Status</th>
                  <th className="px-4 py-3 text-center font-bold">Processados</th>
                  <th className="px-4 py-3 text-center font-bold">Novos</th>
                  <th className="px-4 py-3 text-center font-bold">Vinculados</th>
                  <th className="px-4 py-3 text-center font-bold">Erros</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {historico.map(item => (
                  <tr key={item.id} className="hover:bg-slate-50">
                    <td className="px-4 py-3 text-sm text-slate-600">
                      {new Date(item.dataInicio).toLocaleString('pt-BR')}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <PortalLogo portal={item.portal} size="sm" />
                        <span className="text-sm font-semibold text-slate-900">{item.portal}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-slate-600">{item.tipo}</td>
                    <td className="px-4 py-3">
                      <Badge tone={
                        item.status === 'SUCESSO' ? 'success' :
                        item.status === 'ERRO' ? 'danger' :
                        'warning'
                      }>
                        {item.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3 text-center text-sm font-semibold tabular-nums text-slate-900">{item.totalProcessados}</td>
                    <td className="px-4 py-3 text-center text-sm tabular-nums text-emerald-600">{item.totalNovos}</td>
                    <td className="px-4 py-3 text-center text-sm tabular-nums text-slate-600">{item.totalVinculados}</td>
                    <td className="px-4 py-3 text-center text-sm tabular-nums text-rose-600">{item.totalErros}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* Modal Vincular - Simplificado */}
      {showVincularModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-lg w-full p-6">
            <h3 className="text-lg font-bold tracking-tight text-slate-900 mb-4">Vincular Anúncio</h3>
            <p className="text-sm text-slate-600 mb-4">
              Vincular "{showVincularModal.titulo || `${showVincularModal.marca} ${showVincularModal.modelo}`}" a um veículo do estoque.
            </p>
            <p className="text-sm text-slate-500 mb-4">
              Placa do anúncio: <strong>{showVincularModal.placa || 'Não informada'}</strong>
            </p>

            {showVincularModal.placa && (
              <div className="bg-amber-50 p-3 rounded-lg mb-4">
                <p className="text-sm text-amber-800">
                  Para vincular, informe o ID do veículo no sistema que corresponde a este anúncio.
                </p>
              </div>
            )}

            <div className="mb-4">
              <label className="block text-sm font-semibold text-slate-700 mb-1">ID do Veículo</label>
              <input
                type="number"
                id="veiculoIdInput"
                className="input-field"
                placeholder="Ex: 123"
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="secondary" onClick={() => setShowVincularModal(null)}>
                Cancelar
              </Button>
              <Button
                onClick={() => {
                  const input = document.getElementById('veiculoIdInput') as HTMLInputElement
                  const veiculoId = parseInt(input.value)
                  if (veiculoId && showVincularModal) {
                    vincularAnuncio(showVincularModal.id, veiculoId)
                  }
                }}
                disabled={vinculando !== null}
              >
                {vinculando ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Vincular'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Registrar Anúncio Manual */}
      {showRegistrarModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-xl max-w-xl w-full p-6 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-bold tracking-tight text-slate-900">Registrar Anúncio Publicado</h3>
              <button
                onClick={() => {
                  setShowRegistrarModal(false)
                  setRegistroForm({
                    portal: '',
                    veiculoId: null,
                    urlAnuncio: '',
                    statusPlataforma: 'ATIVO',
                    tipoDestaque: ''
                  })
                  setBuscaVeiculo('')
                  setVeiculosDisponiveis([])
                }}
                className="text-slate-400 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Registre um anúncio que você já publicou em uma plataforma externa para acompanhar no sistema.
            </p>

            <div className="space-y-4">
              {/* Portal */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Portal *</label>
                <div className="grid grid-cols-4 gap-2">
                  {portaisIds.map(portal => (
                    <button
                      key={portal}
                      type="button"
                      onClick={() => setRegistroForm({ ...registroForm, portal })}
                      className={`p-2 rounded-xl border-2 transition-all flex flex-col items-center gap-1 ${
                        registroForm.portal === portal
                          ? 'border-primary-500 bg-primary-50'
                          : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <PortalLogo portal={portal} size="sm" />
                      <span className="text-xs font-semibold">{portal}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Busca de Veículo */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">Veículo *</label>
                <div className="relative">
                  <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    type="text"
                    placeholder="Buscar por placa, marca ou modelo..."
                    value={buscaVeiculo}
                    onChange={(e) => {
                      setBuscaVeiculo(e.target.value)
                      fetchVeiculos(e.target.value)
                    }}
                    className="input-field pl-10"
                  />
                </div>

                {/* Lista de Veículos */}
                {veiculosDisponiveis.length > 0 && (
                  <div className="mt-2 border border-slate-200 rounded-xl divide-y divide-slate-100 max-h-48 overflow-y-auto">
                    {veiculosDisponiveis.map(veiculo => (
                      <button
                        key={veiculo.id}
                        type="button"
                        onClick={() => {
                          setRegistroForm({ ...registroForm, veiculoId: veiculo.id })
                          setBuscaVeiculo(`${veiculo.placa} - ${veiculo.marca} ${veiculo.modelo}`)
                          setVeiculosDisponiveis([])
                        }}
                        className={`w-full p-3 text-left hover:bg-slate-50 flex items-center gap-3 ${
                          registroForm.veiculoId === veiculo.id ? 'bg-primary-50' : ''
                        }`}
                      >
                        <Car className="w-5 h-5 text-slate-400 flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-900 truncate">
                            {veiculo.marca} {veiculo.modelo} {veiculo.versao}
                          </p>
                          <p className="text-sm text-slate-500">
                            Placa: {veiculo.placa} | {veiculo.anoModelo} | R$ {veiculo.valorVenda?.toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Veículo Selecionado */}
                {registroForm.veiculoId && (
                  <div className="mt-2 p-2 bg-emerald-50 rounded-lg flex items-center gap-2">
                    <Car className="w-4 h-4 text-emerald-600" />
                    <span className="text-sm text-emerald-800 flex-1">Veículo ID: {registroForm.veiculoId} selecionado</span>
                    <button
                      type="button"
                      onClick={() => {
                        setRegistroForm({ ...registroForm, veiculoId: null })
                        setBuscaVeiculo('')
                      }}
                      className="text-emerald-600 hover:text-emerald-800"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>

              {/* URL do Anúncio */}
              <div>
                <label className="block text-sm font-semibold text-slate-700 mb-1">URL do Anúncio (opcional)</label>
                <input
                  type="url"
                  placeholder="https://www.olx.com.br/anuncio/..."
                  value={registroForm.urlAnuncio}
                  onChange={(e) => setRegistroForm({ ...registroForm, urlAnuncio: e.target.value })}
                  className="input-field"
                />
                <p className="text-xs text-slate-500 mt-1">Cole a URL do anúncio na plataforma</p>
              </div>

              {/* Status */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Status</label>
                  <select
                    value={registroForm.statusPlataforma}
                    onChange={(e) => setRegistroForm({ ...registroForm, statusPlataforma: e.target.value })}
                    className="input-field"
                  >
                    <option value="ATIVO">Ativo</option>
                    <option value="PAUSADO">Pausado</option>
                    <option value="EXPIRADO">Expirado</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-1">Tipo de Destaque</label>
                  <select
                    value={registroForm.tipoDestaque}
                    onChange={(e) => setRegistroForm({ ...registroForm, tipoDestaque: e.target.value })}
                    className="input-field"
                  >
                    <option value="">Normal</option>
                    <option value="DESTAQUE">Destaque</option>
                    <option value="SUPER_DESTAQUE">Super Destaque</option>
                    <option value="PREMIUM">Premium</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6 pt-4 border-t border-slate-100">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowRegistrarModal(false)
                  setRegistroForm({
                    portal: '',
                    veiculoId: null,
                    urlAnuncio: '',
                    statusPlataforma: 'ATIVO',
                    tipoDestaque: ''
                  })
                  setBuscaVeiculo('')
                  setVeiculosDisponiveis([])
                }}
              >
                Cancelar
              </Button>
              <Button
                onClick={registrarAnuncioExterno}
                disabled={registrando || !registroForm.portal || !registroForm.veiculoId}
              >
                {registrando ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Registrar Anúncio
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
